import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, Sparkles, CheckCircle2, Star, Users, Heart, Building2, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { PHOTOBOOTHS, HOW_IT_WORKS, TESTIMONIALS, CONTACT_INFO } from '../mock';

export const Home = () => {
  const eventTypes = [
    {
      icon: Heart,
      title: 'Mariages',
      description: 'Immortalisez les plus beaux moments',
      image: 'https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=600&h=400&fit=crop'
    },
    {
      icon: PartyPopper,
      title: 'Anniversaires',
      description: 'Célébrez avec style et originalité',
      image: 'https://images.unsplash.com/photo-1544563422-a46c75a351e5'
    },
    {
      icon: Building2,
      title: 'Corporate',
      description: 'Événements d\'entreprise mémorables',
      image: 'https://images.unsplash.com/photo-1727892349154-ac042a8565c7'
    },
    {
      icon: Users,
      title: 'Soirées privées',
      description: 'Créez des souvenirs uniques',
      image: 'https://images.unsplash.com/photo-1765615202063-035f075ab79b'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Visual Impact */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Gold texture background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-captiva-cream via-white to-captiva-cream-dark" />
          <div 
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1545873692-64145c8c42ed?w=1200)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'multiply'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div>
              <div className="inline-flex items-center space-x-2 bg-captiva-cream-dark text-captiva-beige-dark px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4 text-captiva-gold" />
                <span>Location de photobooths premium en Belgique</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                Louez votre photobooth pour un{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-captiva-gold to-captiva-gold-dark">
                  événement inoubliable
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-10 leading-relaxed">
                Mariages • Anniversaires • Entreprises • Soirées privées
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-captiva-gold hover:bg-captiva-gold-dark text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    Réserver mon photobooth
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-captiva-gold text-captiva-gold hover:bg-captiva-cream px-8 py-6 text-lg transition-all duration-300"
                  >
                    Demander un devis
                  </Button>
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-captiva-gold" />
                  <span>Installation incluse</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-captiva-gold" />
                  <span>Photos illimitées</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-captiva-gold" />
                  <span>Toute la Belgique</span>
                </div>
              </div>
            </div>

            {/* Right: Image Showcase */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src="https://images.unsplash.com/photo-1727892349075-401c9df83995?w=600&h=400&fit=crop"
                    alt="Amis au photobooth"
                    className="w-full h-64 object-cover rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-300"
                  />
                  <img
                    src="https://images.unsplash.com/photo-1765615202063-035f075ab79b?w=600&h=300&fit=crop"
                    alt="Célébration"
                    className="w-full h-48 object-cover rounded-2xl shadow-xl hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="space-y-4 pt-8">
                  <img
                    src="https://images.unsplash.com/photo-1763651966651-61a20a742151?w=600&h=300&fit=crop"
                    alt="Groupe d'amis célébrant"
                    className="w-full h-48 object-cover rounded-2xl shadow-xl hover:scale-105 transition-transform duration-300"
                  />
                  <img
                    src="https://images.unsplash.com/photo-1727892349154-ac042a8565c7?w=600&h=400&fit=crop"
                    alt="Groupe festif"
                    className="w-full h-64 object-cover rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white px-6 py-3 rounded-full shadow-xl border-2 border-captiva-gold-light">
                <div className="flex items-center space-x-2">
                  <Star className="h-5 w-5 fill-captiva-gold text-captiva-gold" />
                  <span className="font-bold text-gray-900">500+ événements</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types Section with Images */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Pour tous vos événements
            </h2>
            <p className="text-xl text-gray-600">
              Une solution adaptée à chaque occasion
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {eventTypes.map((event, idx) => {
              const Icon = event.icon;
              return (
                <Card
                  key={idx}
                  className="group overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 hover:border-captiva-gold-light"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 text-white">
                      <Icon className="h-8 w-8 mb-2" />
                      <h3 className="text-xl font-bold">{event.title}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600">{event.description}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Photobooths Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-captiva-cream/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choisissez votre photobooth
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Deux modèles premium pour répondre à tous vos besoins
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {PHOTOBOOTHS.map((booth) => (
              <Card
                key={booth.id}
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 hover:border-captiva-gold-light group"
              >
                <div className="relative h-96 overflow-hidden bg-gray-100">
                  <img
                    src={booth.image}
                    alt={booth.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {booth.premium && (
                    <div className="absolute top-4 right-4 bg-captiva-gold text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-1 shadow-lg">
                      <Sparkles className="h-4 w-4" />
                      <span>Premium</span>
                    </div>
                  )}
                  {/* Image Gallery Preview */}
                  <div className="absolute bottom-4 left-4 right-4 flex space-x-2">
                    {booth.images && booth.images.slice(1, 4).map((img, idx) => (
                      <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden border-2 border-white shadow-md">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{booth.name}</h3>
                  <p className="text-captiva-gold font-medium mb-4">{booth.tagline}</p>
                  <p className="text-gray-600 mb-6 leading-relaxed">{booth.description}</p>

                  <div className="space-y-3 mb-6">
                    {booth.features.slice(0, 4).map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 className="h-5 w-5 text-captiva-gold flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <div>
                      <p className="text-sm text-gray-500">{booth.priceLabel}</p>
                      <p className="text-3xl font-bold text-gray-900">{booth.startingPrice}€</p>
                      <p className="text-sm text-gray-500">TVAC</p>
                    </div>
                    <Link to="/photobooths">
                      <Button className="bg-captiva-gold hover:bg-captiva-gold-dark text-white">
                        En savoir plus
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, rapide et sans stress
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((step, idx) => (
              <div key={step.step} className="relative">
                <Card className="p-8 h-full bg-gradient-to-br from-white to-captiva-cream/30 hover:shadow-xl transition-all duration-300 border-2 hover:border-captiva-gold-light">
                  <div className="flex items-center justify-center w-16 h-16 bg-captiva-gold text-white rounded-2xl text-2xl font-bold mb-6 shadow-lg">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </Card>
                {idx < HOW_IT_WORKS.length - 1 && (
                  <div className="hidden md:block absolute top-1/3 -right-4 text-captiva-gold-light">
                    <ArrowRight className="h-8 w-8" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-captiva-gold hover:bg-captiva-gold-dark text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Commencer maintenant
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials with Images */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-captiva-cream/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des centaines d'événements réussis en Belgique
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.slice(0, 3).map((testimonial) => (
              <Card key={testimonial.id} className="p-6 hover:shadow-xl transition-all duration-300 border-2 hover:border-captiva-gold-light bg-white">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-captiva-gold text-captiva-gold" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.comment}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.event}</p>
                  </div>
                  {testimonial.verified && (
                    <CheckCircle2 className="h-5 w-5 text-captiva-gold" />
                  )}
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/avis">
              <Button variant="outline" size="lg" className="border-2 border-captiva-gold text-captiva-gold hover:bg-captiva-cream">
                Voir tous les avis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA with Background Image */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div 
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1657216328529-3852a5f372cb?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-captiva-beige/95 to-captiva-gold-dark/95 -z-10" />
        
        <div className="max-w-4xl mx-auto text-center relative">
          <Camera className="h-16 w-16 mx-auto mb-6 text-white opacity-90" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Prêt à créer des souvenirs inoubliables ?
          </h2>
          <p className="text-xl mb-10 text-white/90">
            Réservez dès maintenant votre photobooth ou demandez un devis personnalisé
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-white text-captiva-gold-dark hover:bg-gray-100 px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Réserver maintenant
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href={`tel:${CONTACT_INFO.phone}`}>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-captiva-gold-dark px-8 py-6 text-lg transition-all duration-300"
              >
                {CONTACT_INFO.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
